/* ============================================================
   js/battles-engine.js  —  Design Battles Data & Logic Layer
   ============================================================
   ARCHITECTURE LAYER: Application Layer

   Handles:
   - Battle CRUD (create, read, update via Firestore)
   - Submission management (AI / manual / upload)
   - Voting (throttle, dedup, optimistic, cached counts)
   - Leaderboard calculation (top-10, trending)
   - Royalty tracking placeholder

   All Firestore collections:
     battles          — battle metadata (admin-created)
     battle_submissions — user design entries per battle
     battle_votes     — per-user/session vote records

   Usage:
     import { BattlesEngine } from './battles-engine.js';
     const battles = await BattlesEngine.listActiveBattles();
     await BattlesEngine.submitDesign(battleId, { imageUrl, prompt, ... });
     await BattlesEngine.castVote(battleId, submissionId);
   ============================================================ */

import { db, auth } from './firebase.js?v=10';
import {
  collection, doc, addDoc, getDoc, getDocs, updateDoc,
  query, where, orderBy, limit, increment, serverTimestamp, setDoc
} from 'https://www.gstatic.com/firebasejs/10.12.2/firebase-firestore.js';
import { DataCache }           from './cache.js';
import { BATTLES_CONFIG, isDesignBattlesAllowed } from './config.js';

/* ──────────────────────────────────────────────────────────
   BATTLE STATUS CONSTANTS
   ────────────────────────────────────────────────────────── */
export const BATTLE_STATUS = {
  UPCOMING:    'upcoming',    // Not yet open for submissions
  OPEN:        'open',        // Accepting submissions
  VOTING:      'voting',      // Submission closed, voting open
  CLOSED:      'closed',      // Voting ended, winner pending
  COMPLETE:    'complete',    // Winner selected, drop created
};

/* ──────────────────────────────────────────────────────────
   VOTE SESSION KEY
   ────────────────────────────────────────────────────────── */
const VOTE_SESSION_KEY = 'cs_battle_votes_v2';
const VOTE_THROTTLE_MS = 2000;
let _lastVoteTime = 0;

/* ──────────────────────────────────────────────────────────
   BATTLES ENGINE
   ────────────────────────────────────────────────────────── */
class _BattlesEngine {

  /* ──────────────── BATTLES ──────────────── */

  /** List all battles, optionally filtered by status */
  async listBattles(status = null) {
    const cacheKey = `battles_list_${status || 'all'}`;
    return DataCache.getOrFetch(cacheKey, async () => {
      let q = collection(db, 'battles');
      if (status) q = query(q, where('status', '==', status), orderBy('createdAt', 'desc'));
      else        q = query(collection(db, 'battles'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }, BATTLES_CONFIG.BATTLE_LIST_TTL_MS, { staleWhileRevalidate: true });
  }

  /** List only active battles (open or voting) */
  async listActiveBattles() {
    const all = await this.listBattles();
    return all.filter(b => b.status === BATTLE_STATUS.OPEN || b.status === BATTLE_STATUS.VOTING);
  }

  /** Get single battle by ID */
  async getBattle(battleId) {
    return DataCache.getOrFetch(`battle_${battleId}`, async () => {
      const snap = await getDoc(doc(db, 'battles', battleId));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    }, BATTLES_CONFIG.BATTLE_LIST_TTL_MS);
  }

  /** Admin: Create a new battle */
  async createBattle({
    theme, description, submissionDeadline, votingStart, votingEnd,
    royaltyPerSale = BATTLES_CONFIG.DEFAULT_ROYALTY_INR, imageUrl = ''
  }) {
    const data = {
      theme, description,
      submissionDeadline: new Date(submissionDeadline).getTime(),
      votingStart:        new Date(votingStart).getTime(),
      votingEnd:          new Date(votingEnd).getTime(),
      royaltyPerSale,
      imageUrl,
      status:             BATTLE_STATUS.UPCOMING,
      entryCount:         0,
      totalVotes:         0,
      winnerId:           null,
      winnerDropId:       null,
      createdAt:          serverTimestamp(),
      updatedAt:          serverTimestamp(),
    };
    const ref = await addDoc(collection(db, 'battles'), data);
    DataCache.invalidatePrefix('battles_list_');
    return ref.id;
  }

  /** Admin: Update battle status */
  async updateBattleStatus(battleId, status) {
    await updateDoc(doc(db, 'battles', battleId), {
      status, updatedAt: serverTimestamp()
    });
    DataCache.invalidate(`battle_${battleId}`);
    DataCache.invalidatePrefix('battles_list_');
  }

  /** Admin: Set winner */
  async setWinner(battleId, submissionId) {
    await updateDoc(doc(db, 'battles', battleId), {
      winnerId: submissionId,
      status:   BATTLE_STATUS.COMPLETE,
      updatedAt: serverTimestamp(),
    });
    await updateDoc(doc(db, 'battle_submissions', submissionId), {
      isWinner: true, updatedAt: serverTimestamp()
    });
    DataCache.invalidate(`battle_${battleId}`);
    DataCache.invalidatePrefix('battle_submissions_');
    DataCache.invalidatePrefix('battles_list_');
  }

  /* ──────────────── SUBMISSIONS ──────────────── */

  /** Get submissions for a battle, sorted by votes desc */
  async getSubmissions(battleId, page = 1) {
    const cacheKey = `battle_submissions_${battleId}_p${page}`;
    return DataCache.getOrFetch(cacheKey, async () => {
      const q = query(
        collection(db, 'battle_submissions'),
        where('battleId', '==', battleId),
        orderBy('votes', 'desc'),
        limit(BATTLES_CONFIG.PAGE_SIZE * page)
      );
      const snap = await getDocs(q);
      const all  = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      return all.slice((page - 1) * BATTLES_CONFIG.PAGE_SIZE);
    }, BATTLES_CONFIG.BATTLE_ENTRIES_TTL_MS, { staleWhileRevalidate: true });
  }

  /** Get top-10 leaderboard for a battle */
  async getLeaderboard(battleId) {
    return DataCache.getOrFetch(`battle_leaderboard_${battleId}`, async () => {
      const q = query(
        collection(db, 'battle_submissions'),
        where('battleId', '==', battleId),
        orderBy('votes', 'desc'),
        limit(10)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d, i) => ({ id: d.id, rank: i + 1, ...d.data() }));
    }, BATTLES_CONFIG.BATTLE_ENTRIES_TTL_MS, { staleWhileRevalidate: true });
  }

  /**
   * Submit a design to a battle
   * entry: { imageUrl, prompt, vibe, productType, color, title }
   */
  async submitDesign(battleId, entry) {
    if (!isDesignBattlesAllowed()) throw new Error('Design Battles feature is disabled.');
    const user   = auth.currentUser;
    const battle = await this.getBattle(battleId);
    if (!battle) throw new Error('Battle not found.');
    if (battle.status !== BATTLE_STATUS.OPEN) throw new Error('This battle is not accepting submissions.');

    // Check user submission limit
    if (user) {
      const existing = await getDocs(query(
        collection(db, 'battle_submissions'),
        where('battleId', '==', battleId),
        where('uid', '==', user.uid)
      ));
      if (existing.size >= BATTLES_CONFIG.MAX_SUBMISSIONS_PER_USER) {
        throw new Error(`You can submit a maximum of ${BATTLES_CONFIG.MAX_SUBMISSIONS_PER_USER} designs per battle.`);
      }
    }

    const data = {
      battleId,
      uid:         user ? user.uid : 'guest',
      userName:    user ? (user.displayName || user.email?.split('@')[0] || 'Designer') : 'Anonymous',
      imageUrl:    entry.imageUrl    || '',
      prompt:      entry.prompt      || '',
      title:       entry.title       || entry.prompt?.slice(0, 40) || 'Untitled',
      vibe:        entry.vibe        || '',
      productType: entry.productType || 'tshirt',
      color:       entry.color       || 'black',
      votes:       0,
      isWinner:    false,
      royaltyPerSale: battle.royaltyPerSale || BATTLES_CONFIG.DEFAULT_ROYALTY_INR,
      createdAt:   serverTimestamp(),
      updatedAt:   serverTimestamp(),
    };

    const ref = await addDoc(collection(db, 'battle_submissions'), data);

    // Increment battle entry count
    await updateDoc(doc(db, 'battles', battleId), {
      entryCount: increment(1), updatedAt: serverTimestamp()
    });

    // Bust caches
    DataCache.invalidatePrefix(`battle_submissions_${battleId}`);
    DataCache.invalidate(`battle_${battleId}`);
    DataCache.invalidatePrefix('battles_list_');

    return ref.id;
  }

  /* ──────────────── VOTING ──────────────── */

  /**
   * Cast a vote for a submission.
   * Returns { success, optimisticCount, error }
   */
  async castVote(battleId, submissionId) {
    if (!isDesignBattlesAllowed()) return { success: false, error: 'battles_disabled' };

    // Throttle check
    const now = Date.now();
    if (now - _lastVoteTime < VOTE_THROTTLE_MS) {
      return { success: false, error: 'throttled' };
    }

    // Session dedup
    if (this._hasSessionVote(battleId, submissionId)) {
      return { success: false, error: 'already_voted' };
    }

    // Optimistic UI
    const prev = DataCache.getVoteCount(submissionId) ?? 0;
    DataCache.setVoteCount(submissionId, prev + 1);
    _lastVoteTime = now;

    try {
      const user     = auth.currentUser;
      const voterId  = user ? user.uid : await this._getGuestId();
      const voteKey  = `${battleId}_${submissionId}_${voterId}`;
      const voteRef  = doc(db, 'battle_votes', voteKey);

      // Server-side dedup
      const existing = await getDoc(voteRef);
      if (existing.exists()) {
        this._recordSessionVote(battleId, submissionId);
        return { success: false, error: 'already_voted' };
      }

      await setDoc(voteRef, {
        battleId, submissionId, voterId,
        createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, 'battle_submissions', submissionId), {
        votes: increment(1)
      });
      await updateDoc(doc(db, 'battles', battleId), {
        totalVotes: increment(1)
      });

      this._recordSessionVote(battleId, submissionId);
      DataCache.invalidatePrefix(`battle_leaderboard_${battleId}`);

      return { success: true, optimisticCount: prev + 1 };

    } catch (err) {
      // Rollback optimistic
      DataCache.setVoteCount(submissionId, prev);
      return { success: false, error: err.message };
    }
  }

  /** Check if session already voted for this submission */
  _hasSessionVote(battleId, submissionId) {
    try {
      const raw = localStorage.getItem(VOTE_SESSION_KEY);
      const votes = raw ? JSON.parse(raw) : {};
      return !!votes[`${battleId}_${submissionId}`];
    } catch { return false; }
  }

  _recordSessionVote(battleId, submissionId) {
    try {
      const raw   = localStorage.getItem(VOTE_SESSION_KEY);
      const votes = raw ? JSON.parse(raw) : {};
      votes[`${battleId}_${submissionId}`] = Date.now();
      localStorage.setItem(VOTE_SESSION_KEY, JSON.stringify(votes));
    } catch {}
  }

  async _getGuestId() {
    let id = localStorage.getItem('_cs_guest_id');
    if (!id) {
      id = 'g_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem('_cs_guest_id', id);
    }
    return id;
  }

  /* ──────────────── TRENDING ──────────────── */

  /**
   * Get trending submissions (most votes in last 24h window)
   * Since Firestore doesn't support time-windowed queries easily,
   * we approximate by getting top votes from the current week's battles.
   */
  async getTrending(limitCount = 6) {
    return DataCache.getOrFetch('battles_trending', async () => {
      const q = query(
        collection(db, 'battle_submissions'),
        orderBy('votes', 'desc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }, 30_000, { staleWhileRevalidate: true });
  }

  /* ──────────────── LIVE REFRESH ──────────────── */

  /**
   * startLiveVoteRefresh(battleId, onUpdate, intervalMs)
   * Polls vote counts in the background and calls onUpdate(submissionsArray).
   * Returns a stop function.
   */
  startLiveVoteRefresh(battleId, onUpdate, intervalMs = BATTLES_CONFIG.VOTE_POLL_MS) {
    const timer = setInterval(async () => {
      if (document.hidden) return;
      try {
        const q = query(
          collection(db, 'battle_submissions'),
          where('battleId', '==', battleId),
          orderBy('votes', 'desc'),
          limit(BATTLES_CONFIG.PAGE_SIZE)
        );
        const snap = await getDocs(q);
        const fresh = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Update cache
        DataCache.set(`battle_submissions_${battleId}_p1`, fresh, BATTLES_CONFIG.BATTLE_ENTRIES_TTL_MS);
        fresh.forEach(s => DataCache.setVoteCount(s.id, s.votes || 0));
        onUpdate(fresh);
      } catch(e) { console.warn('[Battles] Live refresh failed:', e.message); }
    }, intervalMs);
    return () => clearInterval(timer);
  }

  /* ──────────────── UI HELPERS ──────────────── */

  /** Returns the human-readable battle phase */
  getBattlePhaseLabel(battle) {
    const now = Date.now();
    if (!battle) return '';
    if (now < battle.submissionDeadline) return '🔥 Submissions Open';
    if (now < battle.votingEnd)          return '⚡ Voting Live';
    if (battle.status === BATTLE_STATUS.COMPLETE) return '👑 Winner Crowned';
    return '🔒 Voting Closed';
  }

  /** Returns ms remaining until next deadline */
  getTimeRemaining(battle) {
    const now = Date.now();
    if (now < battle.submissionDeadline) return { phase: 'submit', ms: battle.submissionDeadline - now };
    if (now < battle.votingEnd)          return { phase: 'vote',   ms: battle.votingEnd - now };
    return { phase: 'ended', ms: 0 };
  }

  /** Format ms as HH:MM:SS or D days HH:MM */
  formatCountdown(ms) {
    if (ms <= 0) return '00:00:00';
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sc = s % 60;
    if (d > 0) return `${d}d ${String(h).padStart(2,'0')}h ${String(m).padStart(2,'0')}m`;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sc).padStart(2,'0')}`;
  }
}

export const BattlesEngine = new _BattlesEngine();
export default BattlesEngine;
